import addDays from 'date-fns/addDays'
import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import isSameDay from 'date-fns/isSameDay'
import startOfMonth from 'date-fns/startOfMonth'
import groupBy from 'lodash/groupBy'
import keyBy from 'lodash/keyBy'
import last from 'lodash/last'
import mapValues from 'lodash/mapValues'
import maxBy from 'lodash/maxBy'
import minBy from 'lodash/minBy'
import uniq from 'lodash/uniq'
import { comparer, computed, makeObservable } from 'mobx'
import { getValue } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import { BookingIssue, UUID } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { PeriodKind } from '@new/__global/components/TapeChart/TapeChart.types'
import { HomeBasicView } from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.types'
import { UseSelectRangeProps } from '@new/domains/bookingsTapeChart/hooks/useSelectRange'
import { HomeDatePrices } from '@new/domains/properties/_types'
import { HotelFilterIds } from '@new/domains/hotelTapeChart/config/filtersConfig'

import {
  BaseStore,
  BlockedPeriodType,
  DayIndexes,
  ListItem,
  RowProps,
  TapeChartBookingsStatus,
} from '../__types'
import {
  getBlockedDatesById,
  getColumnOffsets,
  parseDate,
  resolveWithAnimationFrame,
  shortISODate,
} from '../__utils'
import { UNKNOWN_GROUP } from '../config/contants'
import { arrangeBlocks } from '../stores/arrangeBlocks'
import { mergeOverlappingBlocks } from '../stores/mergeOverlappingBlocks'
import { AvailabilityBlock, ListType } from './types'

const LIST_EMPTY_ARRAY: ListItem<ListType>[] = []
const EMPTY_SELECT_RANGE_PROPS: UseSelectRangeProps = {
  blockedDatesById: {},
  blockedDatesWithSingleNight: {},
  datePrices: [],
}

export class TapeChartStateStore {
  constructor(
    private getApiStore: () => BaseStore['apiStore'],
    private getFilterStore: () => BaseStore['filterStore'],
  ) {
    makeObservable<
      TapeChartStateStore,
      'allBookings' | 'allUnassignedBookings' | 'mergedListingBlocks'
    >(this, {
      visibleMonths: computed,
      formattedVisibleDates: computed,
      grid: computed,
      allBookings: computed({ equals: comparer.structural }),
      allUnassignedBookings: computed({ equals: comparer.structural }),
      mergedListingBlocks: computed({ equals: comparer.structural }),
      selectRangeProps: computed,
      relatedBlocks: computed,
      listingsToRender: computed,
      listingsSortedRawResult: computed,
      unassignedBookingsListings: computed,
      unassignedUnfilteredBookingsListings: computed,
      listingsExtendedInfo: computed({ equals: comparer.structural }),
      listingsCountByGroup: computed,
      unassignedListingsCountByGroup: computed,
      listingsGroupCount: computed,
      listingIds: computed({ equals: comparer.structural }),
      visibleDates: computed({ equals: comparer.structural }),
      unassignedBookings: computed,
      unassignedBookingsUnitIds: computed,
      currentRoomTypeWithUnassignedBookings: computed,
      bookingIssues: computed,
      availableDatesFilterIndexes: computed,
    })
  }

  get tapeChartApiStore() {
    return this.getApiStore()
  }

  get filterStore() {
    return this.getFilterStore()
  }

  get visibleMonths() {
    return Object.entries(groupBy(this.visibleDates, it => startOfMonth(it).toISOString())).map(
      ([month, days]) => ({
        month,
        days,
      }),
    )
  }

  private getListingOccupiedDates = computedFn((listingId: string) => {
    const listingBlocks = this.mergedListingBlocks?.[listingId]
    const listinghomeBookings = this.getListingBookings(listingId)

    const editableBlocksDays = (listingBlocks?.editableBlocks ?? []).flatMap(block =>
      eachDayOfInterval({
        start: parseDate(block.period.start),
        end: parseDate(block.period.end),
      }).slice(0, -1),
    )

    const readOnlyBlocksDays = (listingBlocks?.readOnlyBlocks ?? []).flatMap(block =>
      eachDayOfInterval({
        start: parseDate(block.period.start),
        end: parseDate(block.period.end),
      }).slice(0, -1),
    )

    const bookingsDays =
      listinghomeBookings?.flatMap(booking =>
        eachDayOfInterval({
          start: parseDate(booking.checkInTiming.effectiveTime),
          end: parseDate(booking.checkOutTiming.effectiveTime),
        }).slice(0, -1),
      ) ?? []

    return new Set(
      [...editableBlocksDays, ...readOnlyBlocksDays, ...bookingsDays].map(it => shortISODate(it)),
    )
  })

  private getListingUnassignedOccupiedDates = computedFn((listingId: string) => {
    const listingUnassignedBookings = this.getListingUnassignedBookings(listingId) ?? []

    const bookingsDays = listingUnassignedBookings.flatMap(booking =>
      eachDayOfInterval({
        start: parseDate(booking.checkInTiming.effectiveTime),
        end: parseDate(booking.checkOutTiming.effectiveTime),
      }).slice(0, -1),
    )

    return new Set(bookingsDays.map(it => shortISODate(it)))
  })

  get formattedVisibleDates() {
    return this.visibleDates.map(shortISODate)
  }

  get grid() {
    return {
      start: this.formattedVisibleDates[0],
      end: this.formattedVisibleDates[this.formattedVisibleDates.length - 1],
    }
  }

  private get allBookings() {
    return mapValues(this.tapeChartApiStore.allBookingsByHome, bookings =>
      bookings.map(booking => ({
        type: 'booking',
        ...booking,
        ...getColumnOffsets({
          startDate: booking.checkInTiming.effectiveTime,
          endDate: booking.checkOutTiming.effectiveTime,
          visibleDates: this.visibleDates,
        }),
      })),
    )
  }

  private get allUnassignedBookings() {
    return resolveWithAnimationFrame(
      mapValues(this.tapeChartApiStore.allUnassignedBookingsByHome, bookings =>
        bookings.map(booking => ({
          type: 'unassignedBooking',
          ...booking,
          ...getColumnOffsets({
            startDate: booking.checkInTiming.effectiveTime,
            endDate: booking.checkOutTiming.effectiveTime,
            visibleDates: this.visibleDates,
          }),
          status: TapeChartBookingsStatus.unassigned,
        })),
      ),
    )
  }

  private get mergedListingBlocks() {
    const lastVisibleDay = last(this.visibleDates)
    const $dayBeforeStartDate = this.filterStore.startDate
      ? addDays(this.filterStore.startDate, -1)
      : addDays(parseDate(lastVisibleDay), -1)
    const $lastVisibleDate = addDays(parseDate(lastVisibleDay), 1)

    const mergeBlocks = (blocks: AvailabilityBlock[]) =>
      mergeOverlappingBlocks(blocks, {
        getStartDate: block =>
          shortISODate(maxBy([$dayBeforeStartDate, parseDate(block.period.start)], d => +d) ?? 0),
        getEndDate: block =>
          shortISODate(minBy([$lastVisibleDate, parseDate(block.period.end)], d => +d) ?? 0),
      }).map(groupOfBlocks => ({
        id: Array.from(groupOfBlocks.blocks)
          .map(b => b.id)
          .join(','),
        blocks: Array.from(groupOfBlocks.blocks),
        period: groupOfBlocks.period,
        ...getColumnOffsets({
          startDate: groupOfBlocks.period.start ?? '',
          endDate: groupOfBlocks.period.end ?? '',
          visibleDates: this.visibleDates,
        }),
      }))

    return mapValues(this.tapeChartApiStore.blocksByHome, homeBlocks => {
      const { editable: editableBlocks, readOnly: readOnlyBlocks } = groupBy(homeBlocks, block =>
        !block.parent && block.source !== 'BUYOUT' ? 'editable' : 'readOnly',
      )

      const editableBlocksResult = mergeBlocks(editableBlocks).map(it => ({
        ...it,
        type: BlockedPeriodType.EditableBlock,
      }))

      const readOnlyBlocksResult = mergeBlocks(readOnlyBlocks).map(it => ({
        ...it,
        type: BlockedPeriodType.ReadOnlyBlock,
      }))

      return {
        editableBlocks: editableBlocksResult,
        readOnlyBlocks: readOnlyBlocksResult,
      }
    })
  }

  get selectRangeProps(): UseSelectRangeProps {
    const blocks = Object.values(this.tapeChartApiStore.blocksByHome ?? {})?.flat()
    const bookings = Object.values(this.tapeChartApiStore.allBookingsByHome ?? {})?.flat()
    if (!blocks || !bookings) {
      // to not calculate it twice, we calculate
      // only when both are loaded
      return EMPTY_SELECT_RANGE_PROPS
    }
    const mappedBlocks = blocks.map(block => ({
      id: block.id,
      kind: PeriodKind.block,
      homeId: block.homeId,
      startDate: block.period.start,
      endDate: block.period.end,
    }))
    const mappedBookings = bookings.map(booking => ({
      id: booking.id,
      kind: PeriodKind.booking,
      homeId: booking.homeId,
      startDate: booking.checkInTiming.effectiveTime,
      endDate: booking.checkOutTiming.effectiveTime,
    }))

    const allPeriods = [...mappedBlocks, ...mappedBookings]

    return {
      blockedDatesById: getBlockedDatesById(allPeriods),
      blockedDatesWithSingleNight: getBlockedDatesById(allPeriods, true),
      datePrices: Object.values(this.tapeChartApiStore.allRates ?? {}).flat() as HomeDatePrices[],
    }
  }

  get relatedBlocks() {
    if (this.tapeChartApiStore.blocksByHome) {
      return Object.values(this.tapeChartApiStore.blocksByHome).flatMap(blocksObject =>
        blocksObject?.map(blockTape => ({
          id: blockTape?.id,
          relatedIds: [blockTape.parent?.id, blockTape.parent?.bookingId],
        })),
      )
    }
    return []
  }

  // Should be used only to render list
  get listingsToRender() {
    return this.tapeChartApiStore.allListingsToRender ?? LIST_EMPTY_ARRAY
  }

  get listingsSortedRawResult() {
    return this.tapeChartApiStore?.rawListingsResults?.sortedRawResults ?? LIST_EMPTY_ARRAY
  }

  get unassignedBookingsListings() {
    return this.tapeChartApiStore.unfilteredSortedListingsList ?? LIST_EMPTY_ARRAY
  }

  get unassignedUnfilteredBookingsListings() {
    return this.tapeChartApiStore.unfilteredSortedListingsList ?? LIST_EMPTY_ARRAY
  }

  get listingsExtendedInfo() {
    const allListings = this.listingsSortedRawResult?.filter(listing =>
      this.listingIds?.includes(listing.id),
    )
    return (
      this.tapeChartApiStore.listingsExtendedResults?.map(extendedListing => ({
        id: extendedListing.id,
        ...allListings.find(listing => extendedListing.id === listing.id),
        ...extendedListing.home,
      })) ?? []
    )
  }

  get listingsCountByGroup() {
    return this.listingsSortedRawResult.reduce<{ [groupId: UUID]: number }>((acc, listing) => {
      const groupId = listing.group?.id ?? UNKNOWN_GROUP
      acc[groupId] = (acc[groupId] ?? 0) + 1
      return acc
    }, {})
  }

  get unassignedListingsCountByGroup() {
    return this.tapeChartApiStore.unfilteredSortedListingsList?.reduce<{ [groupId: UUID]: number }>(
      (acc, listing) => {
        const groupId = listing.group?.id ?? UNKNOWN_GROUP
        acc[groupId] = (acc[groupId] ?? 0) + 1
        return acc
      },
      {},
    )
  }

  get listingsGroupCount() {
    const uniqueGroupIds = new Set(
      this.listingsSortedRawResult.map(listing => listing.group?.id ?? UNKNOWN_GROUP),
    )
    return uniqueGroupIds.size
  }

  get listingIds() {
    return this.listingsSortedRawResult?.map(listing => listing.id)
  }

  get visibleDates() {
    return eachDayOfInterval({
      start: this.filterStore.startDate as Date,
      end: this.filterStore.endDate as Date,
    })
  }

  get unassignedBookings() {
    return this.tapeChartApiStore.unassignedBookings?.map(unassignedBooking => ({
      type: 'unassignedBooking',
      ...unassignedBooking,
      ...getColumnOffsets({
        startDate: unassignedBooking.checkInTiming.effectiveTime,
        endDate: unassignedBooking.checkOutTiming.effectiveTime,
        visibleDates: this.visibleDates,
      }),
      status: TapeChartBookingsStatus.unassigned,
    }))
  }

  get unassignedBookingsUnitIds() {
    return uniq(this.unassignedBookings?.map(booking => booking.homeId))
  }

  get currentRoomTypeWithUnassignedBookings() {
    const roomTypeId = this.filterStore.computedRouteParams?.[HotelFilterIds.RoomType]?.get()

    const roomType = this.tapeChartApiStore.unassignedBookings?.find(
      booking => booking?.hotelRoomType?.id === roomTypeId,
    )?.hotelRoomType

    if (!roomTypeId) {
      return null
    }

    return roomType?.id
      ? { id: roomType.id, name: roomType.roomKind }
      : { id: roomTypeId, name: 'Unassigned Room Type' }
  }

  get bookingIssues(): Record<Partial<BookingIssue>, number> {
    const issueCounts = {} as Record<Partial<BookingIssue>, number>

    if (!this.tapeChartApiStore.allBookingsByHome) {
      return issueCounts
    }
    Object.values(this.tapeChartApiStore.allBookingsByHome).forEach(homeBookings =>
      homeBookings.forEach(({ issues }) => {
        issues.forEach(issue => {
          issueCounts[issue] = (issueCounts[issue] || 0) + 1
        })
      }),
    )
    return issueCounts
  }

  get availableDatesFilterIndexes(): DayIndexes | undefined {
    const { availableDates } = this.filterStore
    if (!availableDates) {
      return undefined
    }

    const getIndex = (date: Date) =>
      this.visibleDates.findIndex(visibleDate => {
        return isSameDay(visibleDate, date)
      })

    const startDayIndex = getIndex(availableDates.start)
    const endDayIndex = getIndex(availableDates.end) + 1

    if (startDayIndex === -1 || endDayIndex === 0) {
      return undefined
    }

    return {
      startDayIndex,
      endDayIndex,
    }
  }

  getListingRates = computedFn((listingId: string) => {
    if (!this.tapeChartApiStore.blocksByHome || !this.tapeChartApiStore.allRates) {
      return
    }

    const blockedDates = this.getListingOccupiedDates(listingId)

    return keyBy(
      this.tapeChartApiStore.allRates?.[listingId]?.map(rate => ({
        ...rate,
        occupied: blockedDates.has(rate.date),
      })),
      it => it.date,
    )
  })

  getListingUnassignedRates = computedFn((listingId: string) => {
    if (!this.tapeChartApiStore.blocksByHome || !this.tapeChartApiStore.allRates) {
      return
    }

    const blockedDates = this.getListingUnassignedOccupiedDates(listingId)

    return Array.from(blockedDates).reduce((acc, date) => {
      return { ...acc, [date]: { occupied: true } }
    }, {})
  })

  getListingBlocks = computedFn(
    (listingId: string, { skipArranging }: { skipArranging: boolean }) => {
      if (skipArranging) {
        return {
          bookings: undefined,
          blockedPeriods: undefined,
        }
      }

      const bookings = this.getListingBookings(listingId) ?? []
      const { editableBlocks = [], readOnlyBlocks = [] } = this.mergedListingBlocks[listingId] ?? {}

      return computed(() => {
        const arranged = arrangeBlocks({
          _blocks: [...bookings, ...editableBlocks, ...readOnlyBlocks],
        })

        const grouped = groupBy(arranged, item =>
          item.type === 'booking' ? 'bookings' : 'blockedPeriods',
        )

        return {
          bookings: (grouped.bookings ?? []) as Array<(typeof bookings)[number] & RowProps>,
          blockedPeriods: (grouped.blockedPeriods ?? []) as Array<
            ((typeof editableBlocks)[0] | (typeof readOnlyBlocks)[0]) & RowProps
          >,
        }
      }).get()
    },
    { equals: comparer.structural },
  )

  getListingBookings = computedFn((homeId: string) => {
    return this.allBookings?.[homeId]
  })

  getListingUnassignedBookings = computedFn((homeId: string) => {
    return getValue(this.allUnassignedBookings)?.[homeId]
  })

  getRoomTypeBlocks = computedFn(
    (listingId: string, { skipArranging }: { skipArranging: boolean }) => {
      const unassignedBookings = this.getListingUnassignedBookings(listingId)

      if (!unassignedBookings || skipArranging) {
        return undefined
      }

      return computed(
        () =>
          arrangeBlocks({
            _blocks: unassignedBookings,
          }) as unknown as Array<(typeof unassignedBookings)[0] & RowProps>,
        { equals: comparer.structural },
      ).get()
    },
    {
      equals: comparer.structural,
    },
  )

  getBookingById = computedFn((bookingId: string) => {
    const unassignedBookings = this.tapeChartApiStore.unassignedBookings ?? []
    const assignedBookings = this.tapeChartApiStore.bookings ?? []
    return [...assignedBookings, ...unassignedBookings].find(({ id }) => id === bookingId)
  })

  getBlockById = computedFn((blockId?: string) => {
    return this.tapeChartApiStore.allBlocks?.find(({ id }) => id === blockId)
  })

  getListingIndex = computedFn((listingId: UUID) => {
    return this.listingsSortedRawResult.findIndex(({ id }) => id === listingId)
  })

  getListingInfoObject = computedFn((homeId: UUID): HomeBasicView => {
    const home = this.listingsSortedRawResult.find(({ id }) => id === homeId)

    return home
      ? {
          id: home.id,
          name: home.name,
          regionName: home.region?.name ?? UNKNOWN_GROUP,
        }
      : undefined
  })
}
